import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";

import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import EmployeeHome from "@/pages/employee/EmployeeHome";
import MyTimesheets from "@/pages/employee/MyTimesheets";
import MyLeave from "@/pages/employee/MyLeave";
import ReportSickness from "@/pages/employee/ReportSickness";
import EmployeeProfile from "@/pages/employee/EmployeeProfile";
import EmployeeResources from "@/pages/employee/EmployeeResources";
import MyForms from "@/pages/employee/MyForms";
import FillForm from "@/pages/employee/FillForm";
import Forms from "@/pages/admin/Forms";
import ManagerHome from "@/pages/manager/ManagerHome";
import StaffCertificates from "@/pages/admin/StaffCertificates";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import Approvals from "@/pages/admin/Approvals";
import Timesheets from "@/pages/admin/Timesheets";
import Employees from "@/pages/admin/Employees";
import JobRoles from "@/pages/admin/JobRoles";
import Customers from "@/pages/admin/Customers";
import Sites from "@/pages/admin/Sites";
import HolidayRequests from "@/pages/admin/HolidayRequests";
import SickLeave from "@/pages/admin/SickLeave";
import LeaveCalendar from "@/pages/admin/LeaveCalendar";
import Compliance from "@/pages/admin/Compliance";
import Apprentices from "@/pages/admin/Apprentices";
import Materials from "@/pages/admin/Materials";
import SiteProfitability from "@/pages/admin/SiteProfitability";
import CustomerProfitability from "@/pages/admin/CustomerProfitability";
import FinanceDashboard from "@/pages/admin/FinanceDashboard";
import PayrollPeriods from "@/pages/admin/PayrollPeriods";
import RequestServices from "@/pages/admin/RequestServices";
import Reports from "@/pages/admin/Reports";
import SiteReport from "@/pages/admin/SiteReport";
import AdminEmployeeProfile from "@/pages/admin/EmployeeProfile";
import AuditLog from "@/pages/admin/AuditLog";
import RepairLog from "@/pages/admin/RepairLog";
import Settings from "@/pages/admin/Settings";
import Resources from "@/pages/admin/Resources";

import SupportDashboard from "@/pages/amden/SupportDashboard";
import Companies from "@/pages/amden/Companies";
import ClientManage from "@/pages/amden/ClientManage";
import AmdenUsers from "@/pages/amden/AmdenUsers";
import ServiceRequests from "@/pages/amden/ServiceRequests";
import KqmUsers from "@/pages/amden/KqmUsers";
import SupportAccess from "@/pages/amden/SupportAccess";
import EmploymentRules from "@/pages/amden/EmploymentRules";
import SystemLogs from "@/pages/amden/SystemLogs";
import TimesheetDateFix from "@/pages/amden/TimesheetDateFix";
import DeletedItems from "@/pages/amden/DeletedItems";
import OvertimeReview from "@/pages/amden/OvertimeReview";
import OvertimePolicies from "@/pages/amden/OvertimePolicies";

function Splash() {
  return <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>;
}

function homeFor(role) {
  return role === "employee" ? "/app" : role === "amden_super" ? "/amden" : role === "kqm_manager" ? "/manager" : "/admin";
}

function Require({ roles, children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <Splash />;
  if (user === false) return <Navigate to="/login" state={{ from: loc }} replace />;
  if (!roles.includes(user.role)) return <Navigate to={homeFor(user.role)} replace />;
  return children;
}

function RootRedirect() {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (user === false) return <Navigate to="/login" replace />;
  return <Navigate to={homeFor(user.role)} replace />;
}

const ADMIN = ["kqm_admin", "amden_super"];
const MGR = [...ADMIN, "kqm_manager"];

function Shell() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/login/:slug" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/" element={<RootRedirect />} />

      {/* Employee */}
      <Route path="/app" element={<Require roles={["employee"]}><EmployeeHome /></Require>} />
      <Route path="/app/timesheets" element={<Require roles={["employee"]}><MyTimesheets /></Require>} />
      <Route path="/app/leave" element={<Require roles={["employee"]}><MyLeave /></Require>} />
      <Route path="/app/sickness" element={<Require roles={["employee"]}><ReportSickness /></Require>} />
      <Route path="/app/profile" element={<Require roles={["employee"]}><EmployeeProfile /></Require>} />
      <Route path="/app/resources" element={<Require roles={["employee"]}><EmployeeResources /></Require>} />
      <Route path="/app/forms" element={<Require roles={["employee"]}><MyForms /></Require>} />
      <Route path="/app/forms/sub/:sid" element={<Require roles={["employee"]}><FillForm /></Require>} />
      <Route path="/app/forms/:id" element={<Require roles={["employee"]}><FillForm /></Require>} />

      {/* Admin */}
      <Route path="/manager" element={<Require roles={["kqm_manager"]}><ManagerHome /></Require>} />
      <Route path="/admin" element={<Require roles={ADMIN}><AdminDashboard /></Require>} />
      <Route path="/admin/approvals" element={<Require roles={ADMIN}><Approvals /></Require>} />
      <Route path="/admin/timesheets" element={<Require roles={ADMIN}><Timesheets /></Require>} />
      <Route path="/admin/employees" element={<Require roles={MGR}><Employees /></Require>} />
      <Route path="/admin/subcontractors" element={<Require roles={MGR}><Employees key="rct" mode="rct" /></Require>} />
      <Route path="/admin/employees/:id" element={<Require roles={ADMIN}><AdminEmployeeProfile /></Require>} />
      <Route path="/admin/job-roles" element={<Require roles={ADMIN}><JobRoles /></Require>} />
      <Route path="/admin/compliance" element={<Require roles={MGR}><Compliance /></Require>} />
      <Route path="/admin/compliance/certificates" element={<Require roles={MGR}><StaffCertificates /></Require>} />
      <Route path="/admin/apprentices" element={<Require roles={ADMIN}><Apprentices /></Require>} />
      <Route path="/admin/customers" element={<Require roles={ADMIN}><Customers /></Require>} />
      <Route path="/admin/sites" element={<Require roles={MGR}><Sites /></Require>} />
      <Route path="/admin/holidays" element={<Require roles={ADMIN}><HolidayRequests /></Require>} />
      <Route path="/admin/sick-leave" element={<Require roles={ADMIN}><SickLeave /></Require>} />
      <Route path="/admin/leave-calendar" element={<Require roles={MGR}><LeaveCalendar /></Require>} />
      <Route path="/admin/materials" element={<Require roles={ADMIN}><Materials /></Require>} />
      <Route path="/admin/finance" element={<Require roles={ADMIN}><FinanceDashboard /></Require>} />
      <Route path="/admin/site-profitability" element={<Require roles={ADMIN}><SiteProfitability /></Require>} />
      <Route path="/admin/customer-profitability" element={<Require roles={ADMIN}><CustomerProfitability /></Require>} />
      <Route path="/admin/payroll" element={<Require roles={ADMIN}><PayrollPeriods /></Require>} />
      <Route path="/admin/request-services" element={<Require roles={ADMIN}><RequestServices /></Require>} />
      <Route path="/admin/reports" element={<Require roles={ADMIN}><Reports /></Require>} />
      <Route path="/admin/site-report" element={<Require roles={ADMIN}><SiteReport /></Require>} />
      <Route path="/admin/audit" element={<Require roles={ADMIN}><AuditLog /></Require>} />
      <Route path="/admin/repair-log" element={<Require roles={ADMIN}><RepairLog /></Require>} />
      <Route path="/admin/settings" element={<Require roles={ADMIN}><Settings /></Require>} />
      <Route path="/admin/resources" element={<Require roles={MGR}><Resources /></Require>} />
      <Route path="/admin/forms" element={<Require roles={MGR}><Forms /></Require>} />

      {/* Amden */}
      <Route path="/amden" element={<Require roles={["amden_super"]}><SupportDashboard /></Require>} />
      <Route path="/amden/companies" element={<Require roles={["amden_super"]}><Companies /></Require>} />
      <Route path="/amden/companies/:cid" element={<Require roles={["amden_super"]}><ClientManage /></Require>} />
      <Route path="/amden/service-requests" element={<Require roles={["amden_super"]}><ServiceRequests /></Require>} />
      <Route path="/amden/amden-users" element={<Require roles={["amden_super"]}><AmdenUsers /></Require>} />
      <Route path="/amden/users" element={<Require roles={["amden_super"]}><KqmUsers /></Require>} />
      <Route path="/amden/access" element={<Require roles={["amden_super"]}><SupportAccess /></Require>} />
      <Route path="/amden/rules" element={<Require roles={["amden_super"]}><EmploymentRules /></Require>} />
      <Route path="/amden/logs" element={<Require roles={["amden_super"]}><SystemLogs /></Require>} />
      <Route path="/amden/date-fix" element={<Require roles={["amden_super"]}><TimesheetDateFix /></Require>} />
      <Route path="/amden/audit" element={<Require roles={["amden_super"]}><AuditLog /></Require>} />
      <Route path="/amden/bin" element={<Require roles={["amden_super"]}><DeletedItems /></Require>} />
      <Route path="/amden/ot-review" element={<Require roles={["amden_super"]}><OvertimeReview /></Require>} />
      <Route path="/amden/ot-policies" element={<Require roles={["amden_super"]}><OvertimePolicies /></Require>} />

      <Route path="*" element={<RootRedirect />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Shell />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
